import React, { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CAP_OPTIONS = [
  { value: "agents",          label: "Agent Architecture" },
  { value: "retrieval",       label: "Retrieval / RAG" },
  { value: "automation",      label: "Automation" },
  { value: "evals",           label: "Evals" },
  { value: "content",         label: "Content AI" },
  { value: "personalization", label: "Personalization" },
  { value: "ai_consulting",   label: "AI Consulting" },
  { value: "ai_product",      label: "AI Product Mgmt" },
  { value: "ai_dev",          label: "AI Development" },
  { value: "agentic_os",      label: "Agentic OS" },
  { value: "model_training",  label: "Model Training" },
  { value: "inference",       label: "Inference" },
  { value: "web_mobile",      label: "Web & Mobile" },
  { value: "cloud_infra",     label: "Cloud Infra" },
  { value: "data_eng",        label: "Data Engineering" },
];

const CAP_ACCENTS = {
  agents: "#6366F1", retrieval: "#3B82F6", automation: "#16A34A",
  evals: "#A855F7", content: "#CA8A04", personalization: "#0284C7",
  ai_consulting: "#F59E0B", ai_product: "#EC4899", ai_dev: "#10B981",
  agentic_os: "#EF4444", model_training: "#14B8A6", inference: "#06B6D4",
  web_mobile: "#F97316", cloud_infra: "#84CC16", data_eng: "#94A3B8",
};

const GLYPH_OPTIONS = ["tree", "funnel", "page", "scale", "doc", "target", "stack", "handoff"];
const COMPLEXITY_LABELS = { 1: "Foundational", 2: "Composite", 3: "Advanced" };

const SERVES_OPTIONS = [
  "Growth & Marketing",
  "Operations",
  "Product",
  "Customer Success",
  "Customer Support",
  "Finance",
  "Execs",
  "HR",
  "Admin",
];

const EMPTY_FORM = {
  name: "", cap: "agents", glyph: "tree", solves: "", how: "",
  complexity: 1, deployedIn: "", serves: "Product", displayOrder: 0, isVisible: true,
};

// Inline SVG glyph preview (same paths as Craft.jsx — kept local to avoid cross-component imports)
function GlyphPreview({ type }) {
  const paths = {
    tree: <><circle cx="12" cy="5" r="2.2"/><circle cx="6" cy="17" r="2.2"/><circle cx="18" cy="17" r="2.2"/><line x1="12" y1="7.2" x2="7" y2="14.8"/><line x1="12" y1="7.2" x2="17" y2="14.8"/></>,
    funnel: <path d="M3.5 5h17l-6.5 7.5v5l-4-2v-3L3.5 5z"/>,
    page: <><rect x="4" y="2" width="13" height="18" rx="1.5"/><line x1="7.5" y1="8" x2="13.5" y2="8"/><line x1="7.5" y1="11.5" x2="13.5" y2="11.5"/><line x1="7.5" y1="15" x2="11" y2="15"/></>,
    scale: <><line x1="12" y1="3" x2="12" y2="20"/><line x1="5" y1="7" x2="19" y2="7"/><path d="M5 7a3 3 0 0 0 6 0"/><path d="M13 7a3 3 0 0 0 6 0"/><line x1="8" y1="20" x2="16" y2="20"/></>,
    doc: <><path d="M6 2h8l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z"/><path d="M14 2v4h4"/><line x1="8" y1="10" x2="14" y2="10"/><line x1="8" y1="14" x2="14" y2="14"/></>,
    target: <><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5"/></>,
    stack: <><rect x="3" y="15" width="18" height="4" rx="1"/><rect x="5" y="10" width="14" height="4" rx="1"/><rect x="7" y="5" width="10" height="4" rx="1"/></>,
    handoff: <><circle cx="6" cy="12" r="2.5"/><line x1="8.5" y1="12" x2="15" y2="12"/><polyline points="12,8.5 16,12 12,15.5"/><circle cx="18" cy="12" r="2.5"/></>,
  };
  return (
    <div style={{
      width: 32, height: 32, background: "#0c1413", borderRadius: 6,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <svg width={20} height={20} viewBox="0 0 24 24"
        fill="none" stroke="#6FB6AE" strokeWidth={1.7}
        strokeLinecap="round" strokeLinejoin="round">
        {paths[type] || null}
      </svg>
    </div>
  );
}

export default function CraftAdmin() {
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchPatterns = async () => {
    try {
      const res = await axios.get(`${API}/admin/craft`);
      if (res.data.success) setPatterns(res.data.data);
    } catch {
      setError("Failed to load patterns.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPatterns(); }, []);

  const openAdd = () => {
    setForm({ ...EMPTY_FORM });
    setEditingId(null);
    setIsDuplicate(false);
    setShowModal(true);
  };

  const openEdit = (p) => {
    setForm({
      name: p.name || "", cap: p.cap || "agents", glyph: p.glyph || "tree",
      solves: p.solves || "", how: p.how || "", complexity: p.complexity || 1,
      deployedIn: p.deployedIn || "", serves: p.serves || "Product",
      displayOrder: p.displayOrder ?? 0, isVisible: p.isVisible ?? true,
    });
    setEditingId(p.id);
    setIsDuplicate(false);
    setShowModal(true);
  };

  const openDuplicate = (p) => {
    setForm({
      name: p.name || "", cap: p.cap || "agents", glyph: p.glyph || "tree",
      solves: p.solves || "", how: p.how || "", complexity: p.complexity || 1,
      deployedIn: p.deployedIn || "", serves: p.serves || "Product",
      displayOrder: p.displayOrder ?? 0, isVisible: p.isVisible ?? true,
    });
    setEditingId(null);
    setIsDuplicate(true);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.solves.trim() || !form.how.trim() || !form.serves.trim()) {
      alert("Name, What it solves, How it works, and Serves are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        complexity: Number(form.complexity),
        displayOrder: Number(form.displayOrder),
        deployedIn: form.deployedIn.trim() || null,
      };
      if (editingId) {
        await axios.put(`${API}/admin/craft/${editingId}`, payload);
      } else {
        await axios.post(`${API}/admin/craft`, payload);
      }
      setShowModal(false);
      fetchPatterns();
    } catch {
      alert("Save failed. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisibility = async (p) => {
    try {
      await axios.patch(`${API}/admin/craft/${p.id}/visibility`);
      fetchPatterns();
    } catch {
      alert("Visibility update failed.");
    }
  };

  const handleDelete = async (id) => {
    if (deleteConfirm !== id) { setDeleteConfirm(id); return; }
    try {
      await axios.delete(`${API}/admin/craft/${id}`);
      setDeleteConfirm(null);
      fetchPatterns();
    } catch {
      alert("Delete failed.");
    }
  };

  const labelStyle = {
    display: "block", marginBottom: 6, fontSize: 12, fontWeight: 600,
    color: "#374151", fontFamily: "Montserrat, sans-serif",
    textTransform: "uppercase", letterSpacing: "0.5px",
  };
  const inputStyle = {
    width: "100%", padding: "9px 12px", border: "1px solid #D1D5DB",
    borderRadius: 7, fontSize: 14, fontFamily: "Montserrat, sans-serif",
    boxSizing: "border-box", color: "#111827", background: "#fff",
  };
  const thStyle = {
    padding: "10px 14px", textAlign: "left",
    fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 12,
    color: "#6B7280", borderBottom: "1px solid #E5E7EB", whiteSpace: "nowrap",
  };
  const tdStyle = {
    padding: "12px 14px", borderBottom: "1px solid #F3F4F6", verticalAlign: "middle",
  };

  return (
    <div>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h2 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 20, color: "#111827", margin: 0 }}>
            Craft Patterns
          </h2>
          <p style={{ color: "#6B7280", fontSize: 13, margin: "4px 0 0", fontFamily: "Montserrat, sans-serif" }}>
            Manage the AI engineering patterns shown on /craft
          </p>
        </div>
        <button
          onClick={openAdd}
          style={{
            padding: "9px 18px", background: "#2D5C5A", color: "#fff",
            border: "none", borderRadius: 7, cursor: "pointer",
            fontFamily: "Montserrat, sans-serif", fontWeight: 500, fontSize: 13,
          }}
        >
          + Add Pattern
        </button>
      </div>

      {error && <div style={{ color: "#DC2626", marginBottom: 16, fontSize: 14 }}>{error}</div>}

      {loading ? (
        <div style={{ color: "#6B7280", fontSize: 14 }}>Loading…</div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #E5E7EB", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ background: "#F9FAFB" }}>
              <tr>
                {["Glyph","Name","Capability","Complexity","Serves","Order","Status","Actions"].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {patterns.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ ...tdStyle, textAlign: "center", color: "#9CA3AF" }}>
                    No patterns yet. Click "+ Add Pattern" to create one.
                  </td>
                </tr>
              )}
              {patterns.map(p => (
                <tr key={p.id}>
                  <td style={tdStyle}><GlyphPreview type={p.glyph}/></td>
                  <td style={{ ...tdStyle, fontFamily: "Montserrat, sans-serif", fontWeight: 500, fontSize: 13, color: "#111827" }}>
                    {p.name}
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: "2px 8px", borderRadius: 99, fontSize: 11,
                      fontWeight: 500, fontFamily: "Montserrat, sans-serif",
                      background: `${CAP_ACCENTS[p.cap] || "#888"}22`,
                      color: CAP_ACCENTS[p.cap] || "#888",
                    }}>
                      {CAP_OPTIONS.find(c => c.value === p.cap)?.label || p.cap}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, fontSize: 12, color: "#6B7280" }}>
                    {COMPLEXITY_LABELS[p.complexity] || p.complexity}
                  </td>
                  <td style={{ ...tdStyle, fontSize: 12, color: "#6B7280" }}>{p.serves}</td>
                  <td style={{ ...tdStyle, fontSize: 12, color: "#6B7280", textAlign: "center" }}>{p.displayOrder}</td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: "2px 8px", borderRadius: 99, fontSize: 11,
                      fontFamily: "Montserrat, sans-serif",
                      background: p.isVisible ? "#D1FAE5" : "#FEE2E2",
                      color: p.isVisible ? "#065F46" : "#991B1B",
                    }}>
                      {p.isVisible ? "Visible" : "Hidden"}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                    <button onClick={() => openEdit(p)} style={{ marginRight: 6, padding: "4px 10px", borderRadius: 5, border: "none", cursor: "pointer", background: "#EFF6FF", color: "#1D4ED8", fontSize: 12, fontFamily: "Montserrat, sans-serif" }}>Edit</button>
                    <button onClick={() => openDuplicate(p)} style={{ marginRight: 6, padding: "4px 10px", borderRadius: 5, border: "none", cursor: "pointer", background: "#F5F3FF", color: "#6D28D9", fontSize: 12, fontFamily: "Montserrat, sans-serif" }}>Duplicate</button>
                    <button
                      onClick={() => handleToggleVisibility(p)}
                      style={{
                        marginRight: 6, padding: "4px 10px", borderRadius: 5, border: "none", cursor: "pointer",
                        background: p.isVisible ? "#FEF3C7" : "#D1FAE5",
                        color: p.isVisible ? "#92400E" : "#065F46",
                        fontSize: 12, fontFamily: "Montserrat, sans-serif",
                      }}
                    >
                      {p.isVisible ? "Hide" : "Show"}
                    </button>
                    <button
                      onClick={() => handleDelete(p.id)}
                      onBlur={() => { if (deleteConfirm === p.id) setDeleteConfirm(null); }}
                      style={{
                        padding: "4px 10px", borderRadius: 5, border: "none", cursor: "pointer",
                        background: deleteConfirm === p.id ? "#DC2626" : "#FEE2E2",
                        color: deleteConfirm === p.id ? "#fff" : "#991B1B",
                        fontSize: 12, fontFamily: "Montserrat, sans-serif",
                      }}
                    >
                      {deleteConfirm === p.id ? "Confirm" : "Delete"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal overlay */}
      {showModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 100, padding: 20,
        }}>
          <div style={{
            background: "#fff", borderRadius: 12, padding: 32,
            width: "100%", maxWidth: 580, maxHeight: "90vh", overflowY: "auto",
          }}>
            {/* Modal header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <h3 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 16, color: "#111827", margin: 0 }}>
                {editingId ? "Edit Pattern" : isDuplicate ? "Duplicate Pattern" : "Add Pattern"}
              </h3>
              <button onClick={() => { setShowModal(false); setIsDuplicate(false); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#6B7280", lineHeight: 1 }}>×</button>
            </div>

            {/* Name */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Pattern name *</label>
              <input type="text" value={form.name} placeholder="e.g. Supervisor–Subagent Mesh"
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                style={inputStyle}/>
            </div>

            {/* Capability + Glyph */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>Capability *</label>
                <select value={form.cap} onChange={e => setForm(f => ({ ...f, cap: e.target.value }))}
                  style={inputStyle}>
                  {CAP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Glyph *</label>
                <select value={form.glyph} onChange={e => setForm(f => ({ ...f, glyph: e.target.value }))}
                  style={inputStyle}>
                  {GLYPH_OPTIONS.map(g => (
                    <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Solves */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>
                What it solves *{" "}
                <span style={{ textTransform: "none", fontWeight: 400, color: "#9CA3AF" }}>(buyer one-liner)</span>
              </label>
              <textarea value={form.solves} rows={2}
                onChange={e => setForm(f => ({ ...f, solves: e.target.value }))}
                style={{ ...inputStyle, resize: "vertical" }}/>
            </div>

            {/* How */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>
                How it works *{" "}
                <span style={{ textTransform: "none", fontWeight: 400, color: "#9CA3AF" }}>(2–3 sentences)</span>
              </label>
              <textarea value={form.how} rows={4}
                onChange={e => setForm(f => ({ ...f, how: e.target.value }))}
                style={{ ...inputStyle, resize: "vertical" }}/>
            </div>

            {/* Complexity */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Complexity</label>
              <div style={{ display: "flex", gap: 20 }}>
                {[1, 2, 3].map(n => (
                  <label key={n} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, fontFamily: "Montserrat, sans-serif" }}>
                    <input type="radio" name="complexity" value={n}
                      checked={Number(form.complexity) === n}
                      onChange={() => setForm(f => ({ ...f, complexity: n }))}/>
                    {n} · {COMPLEXITY_LABELS[n]}
                  </label>
                ))}
              </div>
            </div>

            {/* Deployed in + Serves */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>Deployed in</label>
                <input type="text" value={form.deployedIn}
                  placeholder="Leave blank if not yet fielded"
                  onChange={e => setForm(f => ({ ...f, deployedIn: e.target.value }))}
                  style={inputStyle}/>
              </div>
              <div>
                <label style={labelStyle}>Serves *</label>
                <select value={form.serves} onChange={e => setForm(f => ({ ...f, serves: e.target.value }))}
                  style={inputStyle}>
                  {SERVES_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>

            {/* Display order + Visible */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 28 }}>
              <div>
                <label style={labelStyle}>Display order</label>
                <input type="number" value={form.displayOrder}
                  onChange={e => setForm(f => ({ ...f, displayOrder: e.target.value }))}
                  style={inputStyle}/>
              </div>
              <div>
                <label style={labelStyle}>Visible</label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginTop: 10, fontSize: 13, fontFamily: "Montserrat, sans-serif" }}>
                  <input type="checkbox" checked={form.isVisible}
                    onChange={e => setForm(f => ({ ...f, isVisible: e.target.checked }))}/>
                  Show on /craft page
                </label>
              </div>
            </div>

            {/* Modal actions */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setShowModal(false); setIsDuplicate(false); }} style={{ padding: "9px 18px", border: "1px solid #D1D5DB", borderRadius: 7, background: "#fff", color: "#374151", cursor: "pointer", fontSize: 13, fontFamily: "Montserrat, sans-serif" }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} style={{ padding: "9px 18px", background: "#2D5C5A", color: "#fff", border: "none", borderRadius: 7, cursor: saving ? "not-allowed" : "pointer", fontSize: 13, fontFamily: "Montserrat, sans-serif", opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving…" : editingId ? "Update Pattern" : isDuplicate ? "Save Duplicate" : "Add Pattern"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
